import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BookmarkSimple,
  Clock,
  X,
  ArrowClockwise,
  Hourglass,
} from "@phosphor-icons/react";
import { PortalShell } from "@/components/PortalShell";
import { ShapePreview } from "@/components/PortalShape";
import { portalApi, portalDate, portalAsset } from "@/lib/portalApi";
import { formatApiErrorDetail } from "@/lib/api";
import { PortalEmpty } from "@/components/PortalEmpty";

const TONE = {
  Requested: "bg-amber-50 text-amber-700",
  Reserved: "bg-teal-50 text-teal-700",
  Fabricating: "bg-yellow-100 text-yellow-700",
  Installed: "bg-emerald-50 text-emerald-700",
  Declined: "bg-slate-100 text-slate-500",
  Cancelled: "bg-slate-100 text-slate-500",
  Expired: "bg-slate-100 text-slate-500",
};

const NOTE = {
  Requested: "We have it aside and are checking it over.",
  Reserved: "Confirmed — it is held in your name.",
  Fabricating: "In the shop being cut.",
  Installed: "Installed. Enjoy it.",
  Declined: "We could not hold this one — talk to us and we will find another.",
  Cancelled: "You released this hold — it is back in the warehouse.",
  Expired:
    "This hold ran out of days, so the piece went back in the warehouse.",
};

const Chip = ({ children }) => (
  <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-500">
    {children}
  </span>
);

const ExpiryLine = ({ row }) => {
  if (!row.expires_at || row.days_left === null) return null;
  const soon = row.days_left <= 3;
  return (
    <div
      data-testid={`portal-reservation-expiry-${row.id}`}
      className={`mt-2 flex items-center gap-1.5 text-xs ${
        soon ? "text-amber-700" : "text-slate-500"
      }`}
    >
      <Hourglass size={13} weight="bold" />
      Held until {portalDate(row.expires_at)} ·{" "}
      {row.days_left === 0
        ? "ends today"
        : `${row.days_left} day${row.days_left === 1 ? "" : "s"} left`}
    </div>
  );
};

const ReleaseDialog = ({ row, busy, onClose, onConfirm }) => (
  <div
    className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-4 backdrop-blur-sm sm:items-center"
    data-testid="portal-release-dialog"
  >
    <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6">
      <div className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-slate-500">
        {row.tag}
        {row.kind ? ` · ${row.kind}` : ""}
      </div>
      <h2 className="mt-1 text-xl font-bold tracking-tight text-slate-900">
        Release the hold on {row.material || "this piece"}?
      </h2>
      <p className="mt-2 text-sm text-slate-500">
        It goes back into the warehouse for anyone to buy. You can always ask us
        to hold it again if it is still there.
      </p>
      <div className="mt-5 flex gap-2">
        <button
          type="button"
          data-testid="portal-release-keep"
          onClick={onClose}
          className="h-12 flex-1 rounded-lg border border-slate-200 text-[11px] font-bold uppercase tracking-widest text-slate-500 transition-colors duration-150 hover:text-slate-900"
        >
          Keep it
        </button>
        <button
          type="button"
          data-testid="portal-release-confirm"
          disabled={busy}
          onClick={onConfirm}
          className="h-12 flex-1 rounded-lg bg-red-600 text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-red-500 disabled:opacity-40"
        >
          {busy ? "Releasing…" : "Un-reserve"}
        </button>
      </div>
    </div>
  </div>
);

export default function PortalReservations() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [asking, setAsking] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    portalApi
      .get("/reservations")
      .then(({ data }) => setRows(data || []))
      .catch(() => setRows([]));

  useEffect(() => {
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const release = async () => {
    setBusy(true);
    try {
      await portalApi.post(`/reservations/${asking.id}/cancel`);
      setRows((list) =>
        list.map((r) =>
          r.id === asking.id
            ? { ...r, status: "Declined", released: true, can_cancel: false }
            : r,
        ),
      );
      toast.success("Released — it is back in the warehouse");
      setAsking(null);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not release that hold",
      );
    }
    setBusy(false);
  };

  const reask = async (row) => {
    setBusy(true);
    try {
      const { data } = await portalApi.post(`/reservations/${row.id}/reask`);
      toast.success(`Asked again — new reference ${data.tag}`);
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not ask for that piece again",
      );
    }
    setBusy(false);
  };

  return (
    <PortalShell
      back="/portal/home"
      micro="Material held for you"
      title="My reservations"
      subtitle="Slabs and remnants we are holding, and the ones you have asked about."
    >
      {loading && (
        <div className="mt-8 space-y-3">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="h-28 animate-pulse rounded-2xl border border-slate-200 bg-white"
            />
          ))}
        </div>
      )}

      {!loading && rows.length === 0 && (
        <PortalEmpty
          className="mt-8"
          testId="portal-reservations-empty"
          icon={BookmarkSimple}
          title="No holds yet"
          text="See a slab or remnant you love? Ask us to hold it and it'll wait for you right here."
          ctaLabel="Browse inventory"
          ctaTo="/portal/inventory"
        />
      )}

      {!loading && rows.length > 0 && (
        <div className="mt-8 space-y-3" data-testid="portal-reservations-list">
          {rows.map((r) => {
            const label = r.expired
              ? "Expired"
              : r.released
                ? "Cancelled"
                : r.status;
            return (
              <div
                key={r.id}
                data-testid={`portal-reservation-${r.id}`}
                className="rounded-2xl border border-slate-200 bg-white p-5 transition-colors duration-200 hover:border-slate-300"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-mono-data text-xs text-slate-500">
                    {r.tag}
                  </span>
                  <span
                    data-testid={`portal-reservation-status-${r.id}`}
                    className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
                      TONE[label] || "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {label}
                  </span>
                  {r.from_portal && (
                    <span className="font-mono-data text-[10px] uppercase tracking-widest text-slate-400">
                      Asked from the portal
                    </span>
                  )}
                  <span className="ml-auto flex items-center gap-1.5 text-xs text-slate-500">
                    <Clock size={13} weight="bold" />{" "}
                    {portalDate(r.created_at) || "—"}
                  </span>
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-900">
                  {r.material || "Material"}{" "}
                  <span className="font-normal text-slate-500">
                    {r.kind ? `· ${r.kind.toLowerCase()}` : ""}
                    {r.code ? ` · ${r.code}` : ""}
                  </span>
                </div>
                <div
                  className="mt-2 flex flex-wrap gap-2"
                  data-testid={`portal-reservation-size-${r.id}`}
                >
                  {r.length_in > 0 && r.width_in > 0 && (
                    <Chip>{r.size} (L x W)</Chip>
                  )}
                  {r.sqft ? <Chip>{r.sqft} ft²</Chip> : null}
                  {r.thickness && <Chip>{r.thickness} thick</Chip>}
                  {r.finish && <Chip>{r.finish}</Chip>}
                  {r.stone && <Chip>{r.stone}</Chip>}
                  {r.quantity > 1 && <Chip>{r.quantity} pieces</Chip>}
                </div>
                <div className="mt-2 text-xs text-slate-500">
                  {r.project_name}
                  {r.install_date
                    ? ` · install ${portalDate(r.install_date)}`
                    : ""}
                </div>
                <ExpiryLine row={r} />
                {(r.photo || (r.polygon || []).length > 2) && (
                  <div
                    className="mt-4 grid gap-3 sm:grid-cols-2"
                    data-testid={`portal-reservation-media-${r.id}`}
                  >
                    {r.photo && (
                      <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                        <img
                          src={portalAsset(
                            `/inventory/photo/${r.thumb || r.photo}`,
                          )}
                          alt={r.material}
                          data-testid={`portal-reservation-photo-${r.id}`}
                          className="h-40 w-full object-cover opacity-90"
                        />
                      </div>
                    )}
                    {(r.polygon || []).length > 2 && (
                      <div className="overflow-hidden rounded-xl border border-slate-200">
                        <ShapePreview
                          polygon={r.polygon}
                          length={r.length_in}
                          width={r.width_in}
                          testId={`portal-reservation-shape-${r.id}`}
                        />
                      </div>
                    )}
                  </div>
                )}
                <p className="mt-3 text-xs leading-relaxed text-slate-500">
                  {NOTE[label] || ""}
                </p>
                {r.comments && (
                  <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-slate-400">
                    {r.comments}
                  </p>
                )}
                {r.can_cancel && (
                  <button
                    type="button"
                    data-testid={`portal-reservation-release-${r.id}`}
                    onClick={() => setAsking(r)}
                    className="mt-4 flex h-11 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 text-[10px] font-bold uppercase tracking-widest text-slate-600 transition-colors duration-150 hover:border-red-300 hover:text-red-600"
                  >
                    <X size={13} weight="bold" /> Un-reserve
                  </button>
                )}
                {r.can_reask && (
                  <button
                    type="button"
                    data-testid={`portal-reservation-reask-${r.id}`}
                    disabled={busy}
                    onClick={() => reask(r)}
                    className="mt-4 flex h-11 items-center gap-2 rounded-lg bg-[#c39e6d] px-4 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-[#a9855a] disabled:opacity-40"
                  >
                    <ArrowClockwise size={13} weight="bold" /> Ask for it again
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {asking && (
        <ReleaseDialog
          row={asking}
          busy={busy}
          onClose={() => setAsking(null)}
          onConfirm={release}
        />
      )}
    </PortalShell>
  );
}
